import { ChevronLeft, ChevronRight, FormatQuote, Verified } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { amazonColors } from '../../../config/amazonTheme';
import { getStaticFileUrl } from '../../../config/apiConfig';
import { getPublicReviews, getPublicStatistics } from '../../../services/reviewService';
import { PublicReviewDto, ReviewStatisticsDto } from '../../../types/review';
import { getHighlightTranslationKey } from '../../../utils/reviewHelpers';
import ReviewDetailModal from './ReviewDetailModal';
import StarRating from './StarRating';

interface PublicReviewsSectionProps {
  maxReviews?: number;
}

const CARD_WIDTH = 280;
const CARD_GAP = 16;

const PublicReviewsSection: React.FC<PublicReviewsSectionProps> = ({
  maxReviews = 12,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [reviews, setReviews] = useState<PublicReviewDto[]>([]);
  const [statistics, setStatistics] = useState<ReviewStatisticsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState<PublicReviewDto | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Number of visible cards based on screen size
  const visibleCards = isMobile ? 1 : isTablet ? 2 : 4;

  // Threshold for showing "Read more" (approximately 3 lines of text)
  const COMMENT_TRUNCATE_LENGTH = 120;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsData, statsData] = await Promise.all([
          getPublicReviews({
            pageNumber: 1,
            pageSize: maxReviews,
            sortBy: 'createdAt',
            sortDirection: 'desc',
          }),
          getPublicStatistics(),
        ]);
        setReviews(reviewsData.items);
        setStatistics(statsData);
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [maxReviews]);

  // Check scroll position
  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
  }, [reviews]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = (CARD_WIDTH + CARD_GAP) * visibleCards;
      const newScrollLeft = direction === 'left'
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const handleScrollEvent = () => {
    updateScrollButtons();
  };

  // Don't render if no reviews
  if (!loading && reviews.length === 0) {
    return null;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Box
      className="notranslate"
      translate="no"
      sx={{
        maxWidth: { xs: '100%', lg: 1200 },
        mx: 'auto',
        mb: 4,
        px: { xs: 1, sm: 2 },
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("reviews.customerReviews")}
          </Typography>
          {statistics && statistics.totalReviews > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <StarRating
                value={statistics.averageRating}
                readonly
                size="small"
                precision={0.5}
              />
              <Typography variant="body2" color="text.secondary">
                {statistics.averageRating.toFixed(1)} {t("reviews.outOf5")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("reviews.reviewsCount", { count: statistics.totalReviews })}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Navigation Arrows - Desktop */}
        {!loading && reviews.length > visibleCards && (
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
            <IconButton
              onClick={() => handleScroll('left')}
              disabled={!canScrollLeft}
              size="small"
              sx={{
                border: `1px solid ${theme.palette.grey[300]}`,
                bgcolor: 'white',
                '&:hover': {
                  bgcolor: theme.palette.grey[50],
                  borderColor: amazonColors.orange,
                },
                '&.Mui-disabled': {
                  bgcolor: theme.palette.grey[100],
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={() => handleScroll('right')}
              disabled={!canScrollRight}
              size="small"
              sx={{
                border: `1px solid ${theme.palette.grey[300]}`,
                bgcolor: 'white',
                '&:hover': {
                  bgcolor: theme.palette.grey[50],
                  borderColor: amazonColors.orange,
                },
                '&.Mui-disabled': {
                  bgcolor: theme.palette.grey[100],
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Reviews Carousel */}
      <Box sx={{ position: 'relative' }}>
        {loading ? (
          <Box sx={{ display: 'flex', gap: 2, overflow: 'hidden' }}>
            {[...Array(visibleCards)].map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                sx={{
                  minWidth: CARD_WIDTH,
                  height: 220,
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>
        ) : (
          <Box
            ref={scrollContainerRef}
            onScroll={handleScrollEvent}
            sx={{
              display: 'flex',
              gap: `${CARD_GAP}px`,
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              scrollBehavior: 'smooth',
              pb: 1,
              // Hide scrollbar
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {reviews.map((review) => {
              const isLongComment = review.comment && review.comment.length > COMMENT_TRUNCATE_LENGTH;
              const hasPhotos = review.photoUrls && review.photoUrls.length > 0;
              const firstPhotoUrl = hasPhotos ? review.photoUrls[0] : null;

              return (
                <Card
                  key={review.id}
                  elevation={0}
                  onClick={() => setSelectedReview(review)}
                  sx={{
                    minWidth: { xs: 'calc(100vw - 52px)', sm: CARD_WIDTH },
                    maxWidth: { xs: 'calc(100vw - 52px)', sm: CARD_WIDTH },
                    height: 290,
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      borderColor: amazonColors.orange,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Header: Name & Rating */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {review.reviewerName}
                          </Typography>
                          {review.isVerified && (
                            <Verified
                              sx={{
                                fontSize: 14,
                                color: amazonColors.orange,
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(review.createdAt)}
                        </Typography>
                      </Box>
                      <StarRating value={review.rating} readonly size="small" />
                    </Box>

                    {/* Content Area */}
                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 140 }}>
                      {/* Photo Thumbnail (show first photo with count indicator) */}
                      {hasPhotos && firstPhotoUrl && (
                        <Box
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            overflow: 'hidden',
                            height: 80,
                            bgcolor: theme.palette.grey[100],
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}
                        >
                          <Box
                            component="img"
                            src={getStaticFileUrl(firstPhotoUrl)}
                            alt="Review photo"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              if (target.parentElement) {
                                target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9e9e9e" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>';
                              }
                            }}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {/* Photo count badge */}
                          {review.photoUrls.length > 1 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 4,
                                right: 4,
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                              }}
                            >
                              +{review.photoUrls.length - 1}
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Comment */}
                      {review.comment && (
                        <Box sx={{ position: 'relative', mb: 1 }}>
                          <FormatQuote
                            sx={{
                              position: 'absolute',
                              top: -6,
                              left: -6,
                              fontSize: 20,
                              color: theme.palette.grey[200],
                              transform: 'rotate(180deg)',
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              pl: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: hasPhotos ? 1 : 6,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.4,
                              fontSize: '0.8rem',
                              mb: 0.5,
                            }}
                          >
                            {review.comment}
                          </Typography>
                          {(hasPhotos || isLongComment) && (
                            <Typography
                              variant="caption"
                              sx={{
                                pl: 1.5,
                                color: amazonColors.orange,
                                fontWeight: 500,
                                display: 'block',
                              }}
                            >
                              {t("reviews.readMore")}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Highlights - Bottom */}
                    {review.highlights && review.highlights.length > 0 && (
                      <Stack 
                        direction="row" 
                        flexWrap="nowrap" 
                        gap={0.5} 
                        sx={{ 
                          mt: 'auto',
                          pt: 1, 
                          overflow: 'hidden',
                          borderTop: `1px solid ${theme.palette.grey[100]}`,
                        }}
                      >
                        {review.highlights.map((highlight) => (
                          <Chip
                            key={highlight}
                            label={t(`reviews.highlights.${getHighlightTranslationKey(highlight)}`)}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              height: 20,
                              bgcolor: theme.palette.grey[100],
                              flexShrink: 0,
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {/* Mobile Navigation Dots or Swipe Hint */}
        {!loading && reviews.length > 1 && isMobile && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 1 }}
          >
            ← {t("reviews.swipeToSeeMore") || "Swipe to see more"} →
          </Typography>
        )}
      </Box>

      {/* Review Detail Modal */}
      <ReviewDetailModal
        open={!!selectedReview}
        onClose={() => setSelectedReview(null)}
        review={selectedReview}
      />
    </Box>
  );
};

export default React.memo(PublicReviewsSection);
